import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { BANNERS } from "@/data/catalog";

const LEAVES = [
  { left: "6%", delay: "0s", duration: "11s", size: 22, color: "#8FB996" },
  { left: "18%", delay: "2.5s", duration: "14s", size: 16, color: "#D4AF37" },
  { left: "32%", delay: "5s", duration: "12s", size: 26, color: "#7C9A82" },
  { left: "47%", delay: "1.2s", duration: "15s", size: 14, color: "#A9C6AE" },
  { left: "61%", delay: "3.8s", duration: "12.5s", size: 20, color: "#D4AF37" },
  { left: "74%", delay: "6.5s", duration: "13s", size: 24, color: "#8FB996" },
  { left: "86%", delay: "0.8s", duration: "16s", size: 15, color: "#7C9A82" },
  { left: "94%", delay: "4.4s", duration: "11.5s", size: 18, color: "#A9C6AE" },
];

const FallingLeaves = () => (
  <div data-testid="falling-leaves" className="absolute inset-0 overflow-hidden pointer-events-none z-10" aria-hidden>
    {LEAVES.map((l, i) => (
      <svg
        key={i}
        viewBox="0 0 24 24"
        fill={l.color}
        className="leaf-particle"
        style={{
          left: l.left,
          width: l.size,
          height: l.size,
          animationDelay: l.delay,
          animationDuration: l.duration,
        }}
      >
        <path d="M20.7 3.3C12.6 4.1 5.8 8.6 4.1 16.6c-.5 2.4.4 4.1 2.2 4.2 8.2.5 13.9-6.6 14.4-17.5zM5.9 18.4c.5-2.9 2.4-6.1 5.4-8.6-2.3 3.1-3.9 6-4.4 8.6h-1z" />
      </svg>
    ))}
  </div>
);

export const HeroCarousel = () => {
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const slides = BANNERS;
  const navigate = useNavigate();

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    api.on("select", onSelect);
    const timer = setInterval(() => api.scrollNext(), 5000);
    return () => {
      api.off("select", onSelect);
      clearInterval(timer);
    };
  }, [api]);

  const goToProduct = (slug) => {
    navigate(slug ? `/products/${slug}` : "/products");
  };

  return (
    <section data-testid="hero-carousel" id="top" className="relative">
      <Carousel setApi={setApi} opts={{ loop: true }} className="w-full">
        <CarouselContent className="-ml-0">
          {slides.map((b, i) => (
            <CarouselItem key={i} className="pl-0">
              <div
                className="relative w-full aspect-video overflow-hidden bg-[#12291e] cursor-pointer"
                onClick={() => goToProduct(b.slug)}
                data-testid={`hero-slide-${b.slug}`}
              >
                {/* Full 16:9 banner art — frame matches image ratio, zero cropping */}
                <img
                  src={b.image}
                  alt={b.title}
                  loading={i === 0 ? "eager" : "lazy"}
                  className="absolute inset-0 w-full h-full object-cover animate-kenburns"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0e241a]/70 via-transparent to-transparent" />
                <FallingLeaves />

                <div className="absolute bottom-0 left-0 right-0 z-20 hidden sm:block">
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 sm:pb-16 flex items-end justify-between gap-4">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span
                        data-testid={`hero-chip-${b.slug}`}
                        className="rounded-full border border-white/25 bg-black/35 backdrop-blur-md text-white text-xs font-semibold uppercase tracking-[0.2em] px-4 py-2"
                      >
                        {b.overline}
                      </span>
                      <Button
                        data-testid={`hero-cta-${b.slug}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          goToProduct(b.slug);
                        }}
                        className="rounded-full bg-[#C05C46] hover:bg-[#a84d3a] text-white px-7 py-5 text-sm sm:text-base font-semibold shadow-xl transition-colors"
                      >
                        {b.cta}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious
          data-testid="hero-prev"
          className="hidden sm:flex left-4 z-30 bg-white/15 border-white/25 text-white hover:bg-white/30 backdrop-blur-md"
        />
        <CarouselNext
          data-testid="hero-next"
          className="hidden sm:flex right-4 z-30 bg-white/15 border-white/25 text-white hover:bg-white/30 backdrop-blur-md"
        />
      </Carousel>
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 z-30">
        {slides.map((_, i) => (
          <button
            key={i}
            data-testid={`hero-dot-${i}`}
            onClick={() => api?.scrollTo(i)}
            aria-label={`Slide ${i + 1}`}
            className={`h-2 rounded-full transition-all duration-300 ${
              current === i ? "w-6 bg-[#D4AF37]" : "w-2 bg-white/60"
            }`}
          />
        ))}
      </div>
    </section>
  );
};
